import React, { useState, useRef } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faSpinner, faCircleCheck, faCircleInfo, faTriangleExclamation, faTableList } from '@fortawesome/free-solid-svg-icons';

const ERROR_GROUP_LABELS = {
  file: 'Fichier',
  columns: 'Colonnes',
  refs: 'Prestataires (Ref)',
  quota: 'Nombre de lignes',
  size: 'Taille du fichier',
};

/**
 * CT upload of a project's free-form "Détails" sheet.
 *
 * The columns are whatever the CT put in the file. The preview reports which of them the
 * app recognised — « Ref » and any column matching a quota dimension — so the CT can see
 * the binding worked before committing. An import REPLACES the project's rows, so an
 * existing sheet is called out explicitly first.
 */
const DetailImportModal = ({ quotaProjectId, existingRows = 0, onClose, onImportComplete }) => {
  // File-import form: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(onClose, { backdropClose: false });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [payload, setPayload] = useState(null); // { file_base64, filename } of the validated file
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmedReplace, setConfirmedReplace] = useState(false);
  const fileInputRef = useRef(null);

  const fileToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('read error'));
    reader.readAsDataURL(f);
  });

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
    setPayload(null);
    setError('');
    setConfirmedReplace(false);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setPreview(null);
    setPayload(null);

    let fileBase64;
    try {
      fileBase64 = await fileToBase64(file);
    } catch (readErr) {
      setError('Impossible de lire le fichier. Il a peut-être été modifié depuis sa sélection — veuillez le re-sélectionner.');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploading(false);
      return;
    }

    try {
      const body = { file_base64: fileBase64, filename: file.name };
      const response = await axios.post(`/quotas/projects/${quotaProjectId}/details/preview`, body);
      setPreview(response.data);
      if (response.data.valid) setPayload(body);
    } catch (err) {
      if (err.response?.data?.errors) {
        const first = Object.values(err.response.data.errors)[0];
        setError(Array.isArray(first) ? first[0] : String(first));
      } else {
        setError(err.response?.data?.message || 'Erreur lors de l\'analyse du fichier.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.valid || !payload) return;
    setCommitting(true);
    setError('');
    try {
      const response = await axios.post(`/quotas/projects/${quotaProjectId}/details/commit`, payload);
      if (onImportComplete) await onImportComplete(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement des détails.');
    } finally {
      setCommitting(false);
    }
  };

  const report = preview?.report || null;
  const errorGroups = preview && preview.errors ? Object.entries(preview.errors) : [];
  // The replace warning only needs acknowledging when there is actually something to lose.
  const needsReplaceAck = (report?.existing_rows ?? existingRows) > 0;
  const canCommit = preview?.valid && payload && (!needsReplaceAck || confirmedReplace);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...dismiss}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faTableList} className="text-indigo-600" />
            Importer les détails (Excel)
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Les colonnes sont libres (ville, scénario, consignes…). Deux sont reconnues automatiquement :
            <strong> Ref</strong> assigne la ligne à un prestataire — la colonne est facultative, et sans elle
            (ou laissée vide) les lignes sont importées sans prestataire puis assignées depuis l'onglet Détails —
            et une colonne portant le nom d'une dimension du quota rattache la ligne à une cellule
            (information seulement : les détails ne comptent jamais dans les quotas).
            Les formules sont enregistrées avec la valeur qu'elles affichaient, pas la formule elle-même.
          </p>

          {existingRows > 0 && !preview && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
              Ce projet contient déjà <strong>{existingRows}</strong> ligne(s) de détails.
              Un nouvel import les <strong>remplace entièrement</strong>, y compris les assignations faites à la main.
            </div>
          )}

          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              onClick={(e) => { e.target.value = null; }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {uploading ? (<><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Analyse...</>)
                : (<><FontAwesomeIcon icon={faUpload} className="mr-2" />Analyser</>)}
            </button>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          {preview && !preview.valid && errorGroups.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                Le fichier contient des erreurs — corrigez-les puis réimportez.
              </div>
              {errorGroups.map(([group, msgs]) => (
                <div key={group} className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-1.5 bg-red-50 text-xs font-semibold text-red-800 uppercase tracking-wide">
                    {ERROR_GROUP_LABELS[group] || group} ({msgs.length})
                  </div>
                  <ul className="px-4 py-2 space-y-1 text-sm text-gray-700 list-disc list-inside max-h-48 overflow-y-auto">
                    {msgs.map((m, idx) => (<li key={idx}>{m}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* What the app recognised in the sheet — the point of the preview. */}
          {report && report.rows > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center justify-between">
                <span>Colonnes reconnues</span>
                {/* Where the parser read from — worth showing when it isn't the obvious
                    place, so a title row or a leading empty sheet can't slip through. */}
                {(report.sheet_index > 1 || report.header_row > 1) && (
                  <span className="normal-case tracking-normal font-normal text-gray-500">
                    feuille {report.sheet_index}
                    {report.sheet_name ? ` « ${report.sheet_name} »` : ''}, en-tête ligne {report.header_row}
                  </span>
                )}
              </div>
              <ul className="px-4 py-3 space-y-2 text-sm">
                {/* A missing Ref column is a normal case, not a problem: the rows import
                    unassigned and are bound from the Détails table afterwards. */}
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon
                    icon={report.ref_column ? faCircleCheck : faCircleInfo}
                    className={`mt-0.5 ${report.ref_column ? 'text-green-600' : 'text-blue-500'}`}
                  />
                  {report.ref_column ? (
                    <span className="text-gray-700">
                      Colonne <strong>{report.ref_column}</strong> reconnue comme prestataire —{' '}
                      {report.assigned} ligne(s) assignée(s), {report.unassigned} à assigner plus tard.
                    </span>
                  ) : (
                    <span className="text-gray-700">
                      Aucune colonne <strong>Ref</strong> dans ce fichier — c'est permis : les {report.rows} ligne(s)
                      seront importées <strong>sans prestataire</strong>, comme si la colonne était vide.
                      Vous pourrez les assigner ensuite, ligne par ligne, dans l'onglet Détails.
                    </span>
                  )}
                </li>

                {report.dimension_columns.map((dc) => (
                  <li key={dc.label} className="flex items-start gap-2">
                    <FontAwesomeIcon
                      icon={dc.values_matched === dc.values_total ? faCircleCheck : faTriangleExclamation}
                      className={`mt-0.5 ${dc.values_matched === dc.values_total ? 'text-green-600' : 'text-amber-500'}`}
                    />
                    <span className="text-gray-700">
                      Colonne <strong>{dc.label}</strong> reconnue comme dimension du quota —{' '}
                      {dc.values_matched}/{dc.values_total} valeur(s) correspondent.
                      {dc.unknown_values.length > 0 && (
                        <span className="text-amber-700"> Inconnues : {dc.unknown_values.join(', ')}.</span>
                      )}
                    </span>
                  </li>
                ))}

                {report.dimension_columns.length === 0 && report.dimensions_total > 0 && (
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 text-gray-400" />
                    <span className="text-gray-600">
                      Aucune colonne ne correspond à une dimension du quota — les lignes ne seront rattachées à aucune cellule.
                    </span>
                  </li>
                )}

                {report.free_columns.length > 0 && (
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 text-green-600" />
                    <span className="text-gray-700">
                      {report.free_columns.length} colonne(s) libre(s) : {report.free_columns.join(', ')}.
                    </span>
                  </li>
                )}

                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 text-green-600" />
                  <span className="text-gray-700">
                    {report.cells_resolved}/{report.rows} ligne(s) rattachée(s) à une cellule de quota (à titre indicatif).
                  </span>
                </li>
              </ul>
            </div>
          )}

          {/* Formulas are stored as the value they displayed, so a lookup left in error in
              Excel arrives as "#N/A". Worth showing: it usually means the file needs
              fixing, not the data. */}
          {report && report.formula_errors && report.formula_errors.length > 0 && (
            <div className="border border-amber-200 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-amber-50 text-xs font-semibold text-amber-800 uppercase tracking-wide">
                Cellules en erreur dans Excel ({report.formula_errors.length})
              </div>
              <ul className="px-4 py-2 space-y-1 text-sm text-gray-700 list-disc list-inside max-h-40 overflow-y-auto">
                {report.formula_errors.map((m, idx) => (<li key={idx}>{m}</li>))}
              </ul>
              <p className="px-4 pb-2 text-xs text-gray-500">
                Ces cellules seront importées telles qu'elles s'affichaient. Corrigez la formule
                dans Excel puis réimportez si ce n'est pas voulu.
              </p>
            </div>
          )}

          {report && report.dropped_columns.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
              Ces colonnes existaient dans l'import précédent et ne sont pas dans ce fichier — leur contenu sera perdu :{' '}
              <strong>{report.dropped_columns.join(', ')}</strong>.
            </div>
          )}

          {preview && preview.valid && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 font-semibold text-sm mb-1">
                <FontAwesomeIcon icon={faCircleCheck} />
                Fichier valide
              </div>
              <p className="text-sm text-green-800">
                {report.rows} ligne(s) et {report.columns.length} colonne(s) seront enregistrées
                {report.existing_rows > 0 ? `, en remplacement des ${report.existing_rows} ligne(s) actuelles` : ''}
                {report.unassigned > 0 ? `, dont ${report.unassigned} sans prestataire (à assigner depuis l'onglet Détails)` : ''}.
              </p>
            </div>
          )}

          {preview && preview.valid && needsReplaceAck && (
            <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmedReplace}
                onChange={(e) => setConfirmedReplace(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Je comprends que les <strong>{report.existing_rows}</strong> ligne(s) de détails actuelles
                (et leurs assignations) seront supprimées et remplacées.
              </span>
            </label>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={committing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canCommit || committing || uploading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {committing ? 'Enregistrement...' : 'Confirmer l\'import'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailImportModal;
